/**
 * ActivityChip — compact "X in your cohort" pill for the top nav.
 *
 * Renders ONLY when:
 *   - user.role === 'student'
 *   - GET /api/manifest/summary returns opted_in: true
 *   - matches_count > 0 (otherwise the chip would say "0 in your cohort", which
 *     is worse than showing nothing — that's what the opt-in prompt is for)
 *
 * Polls every 45 s. Silent-fails on network errors — this is a signal, not a
 * blocker.
 */
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api } from "@/lib/api";
import { Users } from "lucide-react";

const POLL_MS = 45000;

export default function ActivityChip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = () =>
      api.get("/manifest/summary")
         .then((r) => { if (mounted) setData(r.data); })
         .catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (!data || !data.opted_in || (data.matches_count || 0) === 0) return null;

  // Prefer the tightest, most meaningful copy:
  //   1. n at your university (if any)
  //   2. n in your city (if any and no same-uni)
  //   3. n in your cohort
  let label;
  const uni = data.same_university_count || 0;
  const cty = data.same_city_count || 0;
  if (uni > 0)      label = `${uni} at ${data.host_university || "your university"}`;
  else if (cty > 0) label = `${cty} in ${data.host_city || "your city"}`;
  else              label = `${data.matches_count} in your cohort`;

  return (
    <NavLink
      to="/dashboard/manifest"
      data-testid="nav-activity-chip"
      className="hidden lg:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-elec bg-elec/10 border border-elec/20 hover:bg-elec/15 transition-colors px-2.5 py-1 rounded-full"
      title={`${data.matches_count} matched · ${data.pending_requests || 0} pending request${(data.pending_requests || 0) === 1 ? "" : "s"}`}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-elec opacity-70 animate-ping" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-elec" />
      </span>
      {label}
      {data.pending_requests > 0 && (
        <span className="ml-1 text-[10px] text-[color:var(--moe-pink)]">· {data.pending_requests} pending</span>
      )}
    </NavLink>
  );
}
